import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Modal,
  TextField,
  Tooltip,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Breadcrumbs,
  Link,
  IconButton,
  FormControl,
  RadioGroup,
  Radio,
  MenuItem,
  FormControlLabel,
  Select,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Pagination,
  Stack,
} from "@mui/material";
import {
  Folder,
  FolderOpen,
  CloudUpload,
  Add,
  Delete,
  InsertDriveFile,
  ExpandMore,
  NavigateNext,
  ArrowBack,
  Visibility,
  GetApp,
  Description,
  PictureAsPdf,
  Image,
  VideoFile
} from "@mui/icons-material";
import FolderIcon from '@mui/icons-material/Folder';
import { useDispatch, useSelector } from "react-redux";
import FileUploadPopup from './FileUploadPopup';
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { createFolderFilesAction, 
    deleteFolderFilesByCustomerFolderIdAction, 
    deleteFolderFilesByFolderIdAction, 
    getAllFolderFilesAction, 
    getAllFolderFilesCustomerAction,
    getFilesDownloadAction, 
    getFolderFilesAction, 
    uploadFolderFilesAction, 
    resetFolderFiles
} from "../../redux/actions/folderSectionActions";
import { getDetailPolicyAction } from "../../redux/actions/policyActions";

const FolderBreadcrumbs = ({ path, onNavigate }) => {
  if (!path || path.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs
      separator={<NavigateNext fontSize="small" />}
      aria-label="folder navigation breadcrumb"
    >
      <Link
        underline="hover"
        color="inherit"
        onClick={() => onNavigate(null)}
        sx={{ cursor: 'pointer' }}
      >
        Home
      </Link>

      {path.map((folder, index) => {
  const isLast = index === path.length - 1;
  const isRoot = index === 0;
  const displayName = isRoot ? 'Root' : (folder.name || folder.folderName);

  return isLast ? (
    <Typography key={`breadcrumb-${index}`} color="text.primary">
      {displayName}
    </Typography>
  ) : (
    <Link
      key={`breadcrumb-${index}`}
      underline="hover"
      color="inherit"
      onClick={() => onNavigate(folder, index)}
      sx={{ cursor: 'pointer' }}
    >
      {displayName}
    </Link>
  );
})}
    </Breadcrumbs>
  );
};

const ensureSubfolders = (folder) => {
  return folder.subfolders || [];
};

const FolderTree = ({
  folders,
  setFolders,
  level = 0,
  parentPath = [],
  onFolderSelect,
  selectedPath,
  openAddSubfolderModal,
  handleDeleteFolder,
  expandedFolders,
  setExpandedFolders
}) => {

  const dispatch = useDispatch();

  const folderFilesData = useSelector((state) => state.foldersection?.getfiles || {});
  const folderFiles = folderFilesData?.content || [];
  
  const isFolderInPath = (folder, path) => {
    return path.some(item => item.id === folder.id);
  };

  if (!folders || !Array.isArray(folders) || folders.length === 0) {
    return null;
  }

  return (
    <List dense sx={{ pl: level > 0 ? 2 : 0 }}>
      {folders?.length > 0 && folders
      .filter(folder => folder.folderName || folder.name)  
      .map(folder => {
            if (!Array.isArray(folders)) return null;
            const currentPath = [...parentPath, folder];
            const folderId = folder.id || folder.folderId;
            const isExpanded = expandedFolders?.[folderId] || isFolderInPath(folder, selectedPath);
            const isSelected = selectedPath[selectedPath.length - 1]?.id === folder.id;          
            const subfolders = Array.isArray(folder.subfolders) ? folder.subfolders : [];

        return (
          <React.Fragment key={folderId}>
            <ListItem
              disablePadding
              sx={{
                backgroundColor: isSelected ? 'rgba(255, 173, 0, 0.2)' : 'transparent',
                borderRadius: '4px'
              }}
            >
         <ListItemButton
           onClick={() => {
             setExpandedFolders((prev) => {
               const currentlyExpanded = !!prev[folderId];
               const newExpanded = !currentlyExpanded;
         
               // Clone expanded folder state
               let updatedExpanded = { ...prev, [folderId]: newExpanded };
         
               if (newExpanded) {
                 // ✅ Expanding — open folder normally
                 if (folder) {
                   onFolderSelect(currentPath);
                 }
               } else {
                 // ✅ Collapsing logic (handles root + all nested)
                 if (folder) {
                   if (parentPath.length > 0) {
                     // Collapse subfolder: go to parent
                     onFolderSelect([...parentPath]);
                   } else {
                     // Collapse root: clear selection
                     onFolderSelect([]);
                   }
         
                   // 🧹 Recursively collapse ALL child folders & subfolders
                   const collapseAllChildren = (subs) => {
                     subs?.forEach((sf) => {
                       const id = sf.id || sf.folderId;
                       if (id && updatedExpanded[id]) {
                         delete updatedExpanded[id];
                       }
                       if (sf.subfolders?.length) {
                         collapseAllChildren(sf.subfolders);
                       }
                     });
                   };
         
                   // If collapsing root → collapse everything
                   if (folder.subfolders?.length) {
                     collapseAllChildren(folder.subfolders);
                   }
         
                   // If collapsing root itself → remove all expanded flags
                   if (parentPath.length === 0) {
                     updatedExpanded = {};
                   }
                 }
               }
         
               return updatedExpanded;
             });
           }}
           dense
         >
          <FolderIcon
            sx={{
              fontSize: '1.2rem',
              color: isExpanded ? 'black' : '#FFC107',
              marginRight: '12px',
            }}
          />

          <ListItemText
            primary={
              <Box display="flex" alignItems="center" width="100%">
                {level === 0 && parentPath.length === 0
                  ? 'Root'
                  : (folder.folderName || folder.name || '')}

                {(folder.subfolders?.length > 0 || folder.isFolder) && (
                  <ExpandMore
                    sx={{
                      marginLeft: "auto",
                      fontSize: "1.2rem",
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                )}
              </Box>
            }
            primaryTypographyProps={{
              noWrap: true,
              sx: { maxWidth: `${240 - level * 16}px` }
            }}
          />
        </ListItemButton>

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          openAddSubfolderModal(currentPath);
        }}
        sx={{ mr: 0 }}
      >
        <CreateNewFolderIcon sx={{ color: "black" }} fontSize="small" />
      </IconButton>

      {/* Delete Folder */}
      <IconButton
        size="small"
        color="error"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteFolder(folder, currentPath);
        }}
        sx={{ mr: 1 }}
      >
        <Delete fontSize="small" />
      </IconButton>
    </ListItem>

      {isExpanded && (
        <Box sx={{ pl: 2 }}>
          <FolderTree
            folders={subfolders}
            setFolders={setFolders}
            level={level + 1}
            parentPath={currentPath}
            onFolderSelect={onFolderSelect}
            selectedPath={selectedPath}
            openAddSubfolderModal={openAddSubfolderModal}
            handleDeleteFolder={handleDeleteFolder}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
          />
        </Box>
      )}
      {level === 0 && <Divider sx={{ my: 1 }} />}
        </React.Fragment>
       );
      })}
    </List>
  );
};

const getFileIcon = (file) => {
  if (file.isFolder) {
    return <Folder style={{ color: '#FFC107' }} />;
  }

  const fileType = file.fileType || '';
  const fileName = file.filename || file.name || '';
  const extension = fileName.split('.').pop().toLowerCase();

  if (fileType.includes('pdf') || extension === 'pdf') {
    return <PictureAsPdf style={{ color: '#F40F02' }} />;
  } else if (fileType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) {
    return <Image style={{ color: '#34A853' }} />;
  } else if (fileType.includes('video') || ['mp4', 'avi', 'mov', 'wmv'].includes(extension)) {
    return <VideoFile style={{ color: '#4285F4' }} />;
  } else if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
    return <Description style={{ color: '#4285F4' }} />;
  } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
    return <InsertDriveFile style={{ color: '#0F9D58' }} />;
  }

  return <InsertDriveFile style={{ color: '#757575' }} />;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const FolderContents = ({
  currentFolder,
  currentFolderId,
  onOpenUploadPopup,
  handleDeleteFile,
  onFolderSelect,
  selectedPath,
}) => {
  const dispatch = useDispatch();
  const folderFilesData = useSelector(
    (state) => state.foldersection?.getfiles || {}
  );
  const folderFiles = folderFilesData?.content || [];
  const totalCount = folderFilesData?.totalElements || 0;

  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    if (currentFolderId) {
      dispatch(getFolderFilesAction(currentFolderId, page, rowsPerPage));
    }
  }, [dispatch, currentFolderId, page, rowsPerPage]);

  const handlePreview = async (file) => {
    try {
      const fileId = file.id || file.fileId;
      const response = await dispatch(getFilesDownloadAction(fileId, false));
      if (response?.blob) {
        const fileExtension = (file.filename || file.name || "")
          .split(".")
          .pop()
          .toLowerCase();
        const mimeMap = {
          pdf: "application/pdf",
          png: "image/png",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          gif: "image/gif",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        };
        const mimeType = mimeMap[fileExtension] || "application/octet-stream";
        const blob = new Blob([response.blob], { type: mimeType });
        const url = URL.createObjectURL(blob);
        setPreviewFile({ ...file, url, fileType: mimeType });
        setPreviewOpen(true);
      } else if (response?.url) {
        setPreviewFile({ ...file, url: response.url });
        setPreviewOpen(true);
      } else if (file.url) {
        setPreviewFile(file);
        setPreviewOpen(true);
      }
    } catch (error) {
      console.error("Error previewing file:", error);
    }
  };

  const handleOpenFolder = (folder) => {
    const folderId = folder.id || folder.folderId;
    const lastFolder = selectedPath[selectedPath.length - 1];
    const lastId = lastFolder?.id || lastFolder?.folderId;

    if (folderId === lastId) return;

    const newPath = [...selectedPath, folder];
    onFolderSelect(newPath);
  };

  const handleClosePreview = () => {
    if (previewFile?.url?.startsWith("blob:")) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleDownload = async (file) => {
    try {
      setIsDownloading(true);
      const fileId = file.id || file.fileId;
      const response = await dispatch(getFilesDownloadAction(fileId, true));
      const url = response?.url || file.url;
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = file.filename || file.name || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      {currentFolderId ? (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {selectedPath.length === 1
                ? "Root"
                : currentFolder?.folderName || currentFolder?.name || "Folder"}{" "}
              Contents
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={onOpenUploadPopup}
              disabled={!currentFolder}
            >
              Upload File
            </Button>
          </Box>

          {folderFiles.length > 0 ? (
            <Paper sx={{ width: "100%", mb: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="styled-table-heading">Type</TableCell>
                      <TableCell className="styled-table-heading">Name</TableCell>
                      <TableCell className="styled-table-heading">
                        Description
                      </TableCell>
                      <TableCell className="styled-table-heading">
                        File Type
                      </TableCell>
                      <TableCell className="styled-table-heading">
                        Uploaded By
                      </TableCell>
                      <TableCell className="styled-table-heading">Date</TableCell>
                      <TableCell
                        className="styled-table-heading"
                        align="right"
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {folderFiles.map((file, index) => (
                      <TableRow
                        className="styled-table-row"
                        key={`file-${file.id || file.folderId || index}`}
                      >
                        <TableCell>{getFileIcon(file)}</TableCell>
                        <TableCell>
                          {file.isFolder ? (
                            <Link
                              underline="hover"
                              sx={{ cursor: "pointer", fontWeight: "500" }}
                              onClick={() => handleOpenFolder(file)}
                            >
                              {file.folderName}
                            </Link>
                          ) : (
                            file.filename || file.name
                          )}
                        </TableCell>
                        <TableCell>
                          {file.description || file.folderName}
                        </TableCell>
                        <TableCell>{file.type || file.folderName}</TableCell>
                        <TableCell>
                          {file.uploadedByName || "Unknown"}
                        </TableCell>
                        <TableCell>{formatDate(file.createdAt)}</TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            {!file.isFolder && (
                              <>
                                <Tooltip title="Preview">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handlePreview(file)}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    isDownloading ? "Downloading..." : "Download"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleDownload(file)}
                                    disabled={isDownloading}
                                  >
                                    <GetApp fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteFile(file)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalCount > 0 && (
                <Box
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    padding: "16px 0",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    sx={{
                      "@media (max-width: 600px)": {
                        flexDirection: "column",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "12px", mr: 1 }}
                      >
                        Rows per page:
                      </Typography>
                      <Select
                        value={rowsPerPage}
                        onChange={handleChangeRowsPerPage}
                        size="small"
                        sx={{ fontSize: "12px", minWidth: "80px" }}
                      >
                        {[5, 10, 15, 25, 50, 100].map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    <Pagination
                      count={Math.ceil(totalCount / rowsPerPage)}
                      page={page + 1}
                      onChange={(event, value) =>
                        handleChangePage(event, value - 1)
                      }
                      showFirstButton
                      showLastButton
                      siblingCount={1}
                      boundaryCount={1}
                      size="small"
                    />
                  </Stack>
                </Box>
              )}
            </Paper>
          ) : (
            <Box sx={{ p: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() =>
                  onFolderSelect(selectedPath.slice(0, selectedPath.length - 1))
                }
                sx={{ mb: 2 }}
              >
                Back
              </Button>
              <Typography variant="body2" color="text.secondary">
                No files in this folder
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 6,
            px: 2,
            border: "1px dashed #ccc",
            borderRadius: 2,
            bgcolor: "#fafafa",
          }}
        >
        <FolderOpen sx={{ fontSize: 96, color: "gray", mb: 1 }} />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontStyle: "italic", fontWeight: 500 }}
          >
            Select a folder to view contents
          </Typography>
      </Box>
      )}

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          File Preview: {previewFile?.filename || previewFile?.name}
        </DialogTitle>
        <DialogContent dividers>
          {previewFile?.fileType?.includes("image") ? (
            <img
              src={previewFile.url}
              alt="Preview"
              style={{ maxWidth: "100%" }}
            />
          ) : previewFile?.fileType === "application/pdf" ? (
            <iframe
              src={previewFile.url}
              title="PDF Preview"
              width="100%"
              height="500px"
            />
          ) : (
            <Typography>No preview available.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const CustomerFileExplorer = () => {

  const dispatch = useDispatch();

  const [folders, setFolders] = useState([]);
  const [selectedPath, setSelectedPath] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [expandedFolders, setExpandedFolders] = useState({});

  const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
  const [addSubfolderModalOpen, setAddSubfolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [targetPath, setTargetPath] = useState([]);
  const [deleteFolderModalOpen, setDeleteFolderModalOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [folderDeletePath, setFolderDeletePath] = useState([]);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteFileModalOpen, setDeleteFileModalOpen] = useState(false);

  const containerRef = useRef(null);
  const folderTreeRef = useRef(null);
  const contentRef = useRef(null);

  const [fileType, setFileType] = useState('customer');
  const tabSnapshotsRef = useRef({ customer: null, policy: null });
  const suppressAutoFetchRef = useRef(false);

  const folderFilesContent = useSelector((state) => state.foldersection?.getfiles || []);
  const folderFiles = folderFilesContent?.content || []
  const selected_customer_list = useSelector((state) => state?.globalSearchReducer?.customer_data.content || []);  

  const customizedForm = useSelector((state) => state.policy?.allDetailPolicy);
  const clientCustomerId = customizedForm?.clientCustomerId || null;
  const policyId = customizedForm?.id || null;

  const safeCustomizedForm = Array.isArray(customizedForm)
  ? customizedForm
  : customizedForm
  ? [customizedForm]
  : [];


  const [fileUploadPopupOpen, setFileUploadPopupOpen] = useState(false);

useEffect(() => {
  if (customizedForm?.id) {    
    dispatch(getDetailPolicyAction(customizedForm.id));
  }
}, [customizedForm?.id, dispatch]);

  
  const handleFileUpload = async (formData) => {
    try {
      const response = await dispatch(uploadFolderFilesAction(formData));

      showNotification("File uploaded successfully!", "success");

      if (currentFolderId) {
        const folderResponse = await dispatch(getFolderFilesAction(currentFolderId));

        if (folderResponse) {
          let updatedFolder = folderResponse;
          if (Array.isArray(folderResponse) && folderResponse.length > 0) {
            updatedFolder = folderResponse[0];
          }

          if (!updatedFolder.files) {
            updatedFolder.files = [];
          }

          if (response.id || response.fileId) {
            const fileExists = updatedFolder.files.some(file =>
              (file.id === response.id) || (file.fileId === response.fileId)
            );

            if (!fileExists) {
              updatedFolder.files.push(response);
            }
          }

          setCurrentFolder({...updatedFolder});

          if (selectedPath.length > 0) {
            const newPath = [...selectedPath];
            newPath[newPath.length - 1] = updatedFolder;
            setSelectedPath(newPath);
          }
        }
      }

      setFileUploadPopupOpen(false);

    } catch (error) {
    }
  };

  const policies = selected_customer_list.map((customer) => ({
    id: customer.id,
    policyNumber: customer.policyNumber
  }));
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');

  const handlePolicyChange = (e) => {
  setSelectedPolicyId(e.target.value);
};

  const refreshSpecificFolder = async (folderId) => {
    if (!folderId) return;

    try {
      const folderResponse = await dispatch(getFolderFilesAction(folderId));

      if (!folderResponse) return;

      const updateFolderInTree = (foldersList, targetId) => {
        return foldersList.map(folder => {
          const currentId = folder.folderId || folder.id;

          if (currentId === targetId) {
            return {
              ...folder,
              ...folderResponse, 
              subfolders: folderResponse.subfolders || folder.subfolders || []
            };
          } else if (folder.subfolders && folder.subfolders.length > 0) {
            return {
              ...folder,
              subfolders: updateFolderInTree(folder.subfolders, targetId)
            };
          }
          return folder;
        });
      };

      setFolders(prevFolders => updateFolderInTree(prevFolders, folderId));

      if (currentFolderId === folderId) {
        setCurrentFolder(folderResponse);
      }

    } catch (error) {
    }
  };

const fetchFolderWithSubfolders = async (folderId) => {
  if (!folderId) return null;  

  try {
    const folderData = await dispatch(getFolderFilesAction(folderId));
    if (!folderData) return null;

    let folderArray = Array.isArray(folderData) ? folderData : [folderData];

    return await Promise.all(folderArray.map(async (folder) => {
      const id = folder.folderId || folder.id;
      if (!id) return folder;  

      if (folder.subfolders && folder.subfolders.length > 0) {
        const subfolders = await Promise.all(
          folder.subfolders.map((sf) =>
            fetchFolderWithSubfolders(sf.folderId || sf.id)
          )
        );
        return { ...folder, subfolders: subfolders.filter(Boolean) };
      }

      return { ...folder, subfolders: [] };
    }));
  } catch (error) {
    return [];
  }
};


useEffect(() => {
  const raw = sessionStorage.getItem(`fileExplorerState:${clientCustomerId}`);
  if (!raw) return;
  try {
    const s = JSON.parse(raw);
    suppressAutoFetchRef.current = true;      
    setFileType(s.fileType || 'customer');
    setFolders(s.folders || []);
    setSelectedPath(s.selectedPath || []);
    setCurrentFolder(s.currentFolder || null);
    setCurrentFolderId(s.currentFolderId || null);
    setExpandedFolders(s.expandedFolders || {});
    setSelectedPolicy(s.fileType === 'policy' ? (s.selectedPolicy || null) : null);
  } catch {}
}, [clientCustomerId]);

useEffect(() => {
  const data = {
    fileType,
    folders,
    selectedPath,
    currentFolder,
    currentFolderId,
    expandedFolders,
    selectedPolicy,
  };
  sessionStorage.setItem(`fileExplorerState:${clientCustomerId}`, JSON.stringify(data));
}, [
  fileType,
  folders,
  selectedPath,
  currentFolder,
  currentFolderId,
  expandedFolders,
  selectedPolicy,
  clientCustomerId,
]);

 const refreshFolderData = async () => {
  let rootResponse;
  try {
    if (fileType === "policy") {
      const policyId = selectedPolicyId;
      if (!policyId) return;
      rootResponse = await dispatch(getAllFolderFilesAction(policyId));
    } else if (fileType === "customer") {
      if (!clientCustomerId) return;
      rootResponse = await dispatch(getAllFolderFilesCustomerAction(clientCustomerId));
    } else {
      return;
    }
  
      let rootFolders = [];
      if (Array.isArray(rootResponse)) {
        rootFolders = rootResponse;
      } else if (rootResponse && typeof rootResponse === 'object') {
        rootFolders = [rootResponse];
      }
  
      const processedFolders = [];
  
      for (const rootFolder of rootFolders) {
        const folderId = rootFolder?.folderId || rootFolder?.id;
        if (!folderId) continue;
  
        const fullFolder = {
          ...rootFolder,
          subfolders: await fetchFolderWithSubfolders(folderId)
        };
  
        processedFolders.push(fullFolder);
      }
  
      setFolders(processedFolders);
  
      if (selectedPath.length > 0) {
        await refreshCurrentFolder();
      }
    } catch (error) {
    }
  };

  const refreshCurrentFolder = async () => {
    if (!selectedPath.length) return;

    const currentFolderId = selectedPath[selectedPath.length - 1]?.id ||
                           selectedPath[selectedPath.length - 1]?.folderId;

    if (!currentFolderId) return;

    try {
      const folderResponse = await dispatch(getFolderFilesAction(currentFolderId));

      if (!folderResponse) {
        return;
      }

      const updatedFolder = folderResponse;

      const newPath = [...selectedPath];
      newPath[newPath.length - 1] = updatedFolder;
      setSelectedPath(newPath);

    } catch (error) {
    }
  };

  useEffect(() => {
    if (suppressAutoFetchRef.current) {
      suppressAutoFetchRef.current = false;
      return;
    }
    if (fileType === "customer" && clientCustomerId) {
      refreshFolderData();
    } else if (fileType === "policy" && selectedPolicyId) {
      refreshFolderData();
    }
  }, [fileType, clientCustomerId, selectedPolicy, dispatch]);

  useEffect(() => {
    if (selectedPath.length === 0) {
      setCurrentFolder(null);
      setBreadcrumbPath([]);
      setCurrentFolderId(null);
    } else {
      const selected = selectedPath[selectedPath.length - 1];
      setCurrentFolder(selected);
      setBreadcrumbPath(selectedPath);
      const folderId = selected?.folderId || selected?.id;
      setCurrentFolderId(folderId);
    }
  }, [selectedPath]);

  const handleFolderSelect = async (path) => {
    if (!path || path.length === 0) return;
  
    const selectedFolder = path[path.length - 1];
    const folderId = selectedFolder?.folderId || selectedFolder?.id;
  
    setSelectedPath(path); 
    if (!folderId) return;
  
    try {
      const folderResponse = await dispatch(getFolderFilesAction(folderId));
  
      const subfolders = (folderResponse?.content || [])
      .filter(item => item.isFolder)
      .map(folder => ({
        ...folder,
        folderName: folder.folderName || folder.name || "Unnamed Folder",
      }));
  
      const updateFolderInTree = (foldersList) => {
        return foldersList.map(folder => {
          const currentId = folder.folderId || folder.id;
  
          if (currentId === folderId) {
            return {
              ...folder,
              subfolders,
            };
          } else if (Array.isArray(folder.subfolders) && folder.subfolders.length > 0) {
            return {
              ...folder,
              subfolders: updateFolderInTree(folder.subfolders)
            };
          }  
          return folder;
        });
      };
  
      setFolders(prevFolders => updateFolderInTree(prevFolders));
      const newSelectedPath = [...path];
      newSelectedPath[newSelectedPath.length - 1] = {
        ...selectedFolder,
        ...folderResponse 
      };
      setSelectedPath(newSelectedPath);
  
    } catch (error) {
      console.error("Error fetching folder data on selection:", error);
    }
  };
  
  const openAddSubfolderModal = (path) => {
    setTargetPath(path);
    setAddSubfolderModalOpen(true);
    setNewFolderName("");
  };

  const showNotification = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const [explorerHeight, setExplorerHeight] = useState("100vh");
  const [minHeight, setMinHeight] = useState("500px");

  const calculateHeight = () => {
    if (containerRef.current) {
      const windowHeight = window.innerHeight;
      const containerTop = containerRef.current.getBoundingClientRect().top;

      const availableHeight = windowHeight - containerTop - 20;

      const minHeightValue = Math.min(500, availableHeight);
      setMinHeight(`${minHeightValue}px`);

      const foldersHeight = folderTreeRef.current ? folderTreeRef.current.scrollHeight : 0;
      const contentHeight = contentRef.current ? contentRef.current.scrollHeight : 0;

      const contentTotalHeight = Math.max(foldersHeight, contentHeight) + 40;

      const calculatedHeight = Math.min(contentTotalHeight, availableHeight);

      const finalHeight = Math.max(calculatedHeight, minHeightValue);
      setExplorerHeight(`${finalHeight}px`);
    }
  };

  useEffect(() => {
    calculateHeight();

    const handleResize = () => {
      calculateHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [folders, selectedPath, currentFolder]);


  const addNewFolder = async () => {
  const trimmedName = newFolderName.trim();

  if (!trimmedName) {
    showNotification("Folder name cannot be empty", "error");
    return;
  }

  const newFolder = {
    folderName: trimmedName,
    isSubFolder: false
  };

  if (fileType === "policy") {
    const fetchPolicyId = Number(selectedPolicyId);
    if (!fetchPolicyId) {
      showNotification("Please select a policy to create a folder.", "error");
      return;
    }
    newFolder.policyId = fetchPolicyId;

  } else if (fileType === "customer") {
  if (!clientCustomerId) {
    showNotification("Customer ID not found. please check ", "error");
    return;
  }
  newFolder.clientCustomerId = clientCustomerId;
}

  try {
    await dispatch(createFolderFilesAction(newFolder));
    showNotification("Folder created successfully!", "success");
    setAddFolderModalOpen(false);
    setNewFolderName("");
    await refreshFolderData();
  } catch (error) {
    showNotification("Error creating folder. Please try again.", "error");
  }
};
  
  const addNewSubfolder = async () => {
    const trimmedName = newFolderName.trim();
  
    if (!trimmedName) {
      showNotification("Subfolder name cannot be empty", "error");
      return;
    }
  
    if (!targetPath.length) {
      showNotification("Parent folder path not found.", "error");
      return;
    }
    const parentFolder = targetPath[targetPath.length - 1];

    const parentFolderId =
    parentFolder?.folderId !== undefined
    ? parentFolder.folderId
    : parentFolder?.id !== undefined
    ? parentFolder.id
    : null;
    
    if (!parentFolderId) {
      showNotification("Invalid parent folder ID.", "error");
      return;
    }
  
    const newSubfolderData = {
      folderName: trimmedName,
      isSubFolder: true,
      parentFolderId
    };
  
    if (fileType === "policy") {
      const fetchPolicyId = Number(selectedPolicyId);
      if (!fetchPolicyId) {
        showNotification("Please select a policy.", "error");
        return;
      }
      newSubfolderData.policyId = fetchPolicyId;
    } else if (fileType === "customer") {
      if (!clientCustomerId) {
        showNotification("Customer ID not found.", "error");
        return;
      }
      newSubfolderData.clientCustomerId = clientCustomerId;
    }
  
    try {
      const response = await dispatch(createFolderFilesAction(newSubfolderData));
  
      showNotification("Subfolder created successfully!", "success");
      setAddSubfolderModalOpen(false);
      setNewFolderName("");
  
      const newSubfolderObject = {
        folderId: response.folderId || response.id,
        folderName: trimmedName,
        isSubFolder: true,
        parentFolderId,
        subfolders: [],
        files: []
      };
  
      updateFolderTreeWithNewSubfolder(parentFolderId, newSubfolderObject);
  
      if (currentFolderId === parentFolderId) {
        await dispatch(getFolderFilesAction(currentFolderId));
      }
      await refreshSpecificFolder(parentFolderId);
  
      triggerRefresh();
    } catch (error) {
      showNotification("Error creating subfolder. Please try again.", "error");
    }
  };

  useEffect(() => {
    if (folderTreeRef.current) {
    
      folderTreeRef.current.forceUpdate && folderTreeRef.current.forceUpdate();
    }
  }, [folders]);


const updateFolderTreeWithNewSubfolder = (parentFolderId, newSubfolder) => {
  const updateFolders = (foldersList) => {
    return foldersList.map(folder => {
      const currentId = folder.folderId || folder.id;

      if (currentId === parentFolderId) {
        return {
          ...folder,
          subfolders: [...(folder.subfolders || []), newSubfolder]
        };
      } else if (folder.subfolders?.length) {
        return {
          ...folder,
          subfolders: updateFolders(folder.subfolders)
        };
      }

      return folder;
    });
  };

  setFolders(prevFolders => updateFolders(prevFolders));
};
  
const handleBreadcrumbNavigation = (folder, index) => {
  if (folder === null) {
    setSelectedPath([]);
  } else {
    setSelectedPath(breadcrumbPath.slice(0, index + 1));
  }
};
 const handleDeleteFile = (file) => {
  setFileToDelete(file);
  setDeleteFileModalOpen(true);
};

const confirmDeleteFile = async () => {
  if (!fileToDelete) return;
  setDeleteFileModalOpen(false);
  setFileToDelete(null);

  const fileId = fileToDelete.id || fileToDelete.fileId;
  const folderPath = fileToDelete.fullPath;

  let response;

  if (!fileId || !folderPath) {
    showNotification("Missing fileId or folderPath.", "error");
    return;
  }

  try {
    if (fileType === "policy" && selectedPolicyId) {
      response = await dispatch(
        deleteFolderFilesByFolderIdAction(fileId, selectedPolicy.id, folderPath)
      );
    } else if (fileType === "customer" && clientCustomerId) {
      response = await dispatch(
        deleteFolderFilesByCustomerFolderIdAction(fileId, clientCustomerId, folderPath )
      );
    } else {
      showNotification("Missing relevant ID for file deletion.", "error");
      return;
    }

    if (response) {
      showNotification("File deleted successfully", "success");
      setDeleteFileModalOpen(false);
      refreshFolderData();
    } else {
      showNotification("Failed to delete file", "error");
    }
  } catch (error) {
    showNotification(`Error deleting file: ${error.message || "Unknown error"}`, "error");
  }
};

  const openDeleteFolderModal = (folder, path) => {
    setFolderToDelete(folder);
    setFolderDeletePath(path);
    setDeleteFolderModalOpen(true);
  };

 const [refreshTrigger, setRefreshTrigger] = useState(0);

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    setTimeout(calculateHeight, 50);
  };


const handleDeleteFolder = async () => {
  if (!folderToDelete) return;

  const folderId = folderToDelete.id || folderToDelete.folderId;
  let folderPath = folderToDelete?.fullPath;

  if (!folderPath && selectedPath?.length > 0) {
    const index = selectedPath.findIndex(
      f => (f.id || f.folderId) === folderId
    );
    if (index >= 0) {
      folderPath = selectedPath
        .slice(0, index + 1)
        .map(f => f.folderName || f.name)
        .join("/");
    }
  }

  if (!folderId || !folderPath) {
    showNotification("Missing folderId or folderPath.", "error");
    return;
  }

  setDeleteFolderModalOpen(false);
  setFolderToDelete(null);

  try {
    let response;

    if (fileType === "policy" && selectedPolicyId) {
      response = await dispatch(
        deleteFolderFilesByFolderIdAction(folderId, selectedPolicy.id, folderPath)
      );
    } else if (fileType === "customer" && clientCustomerId) {
      response = await dispatch(
        deleteFolderFilesByCustomerFolderIdAction(folderId, clientCustomerId, folderPath)
      );
    }

if (response) {
  showNotification(
    `Folder "${folderToDelete.name || folderToDelete.folderName}" deleted successfully`,
    "success"
  );

  const removeFolderFromTree = (foldersList, folderIdToDelete) => {
    if (!foldersList) return [];
    return foldersList
      .filter(folder => (folder.folderId || folder.id) !== folderIdToDelete) 
      .map(folder => ({
        ...folder,
        subfolders: folder.subfolders
          ? removeFolderFromTree([...folder.subfolders], folderIdToDelete)
          : []
      }));
  };

  setFolders(prevFolders => {
    const updated = removeFolderFromTree(prevFolders, folderId);
    return JSON.parse(JSON.stringify(updated)); 
  });

  if (
    selectedPath.length > 0 &&
    (selectedPath[selectedPath.length - 1].id === folderId ||
      selectedPath[selectedPath.length - 1].folderId === folderId)
  ) {
    if (selectedPath.length > 1) {
      setSelectedPath(selectedPath.slice(0, selectedPath.length - 1));
    } else {
      setSelectedPath([]);
    }
  }

  setExpandedFolders(prev => {
    const newExpanded = { ...prev };
    delete newExpanded[folderId];
    return newExpanded;
  });
}

  } catch (error) {
    showNotification(`Error deleting folder: ${error.message || "Unknown error"}`, "error");
  }
};

 return (
    <Box>
      <Box sx={{ mb: 2 , ml:"-10px"}}>
        <FormControl>
          <RadioGroup
            row
            value={fileType}
            onChange={(e) => {
            const nextType = e.target.value;

            tabSnapshotsRef.current[fileType] = {
              folders,
              selectedPath,
              currentFolder,
              currentFolderId,
              expandedFolders,
              selectedPolicy,
            };

            setFileType(nextType);

            const restore = tabSnapshotsRef.current[nextType];
            if (restore) {
              suppressAutoFetchRef.current = true; 
              setFolders(restore.folders || []);
              setSelectedPath(restore.selectedPath || []);
              setCurrentFolder(restore.currentFolder || null);
              setCurrentFolderId(restore.currentFolderId || null);
              setExpandedFolders(restore.expandedFolders || {});
              setSelectedPolicy(nextType === 'policy' ? (restore.selectedPolicy || null) : null);
            } else {
              setFolders([]);
              setSelectedPath([]);
              setCurrentFolder(null);
              setCurrentFolderId(null);
              setExpandedFolders({});
              if (nextType !== 'policy') setSelectedPolicy(null);
            }
          }}
          >
             <FormControlLabel
              value="customer"
              control={<Radio />}
              label="Customer Files"
            />
            <FormControlLabel
              value="policy"
              control={<Radio />}
              label="Policy Files"
            />
            
          </RadioGroup>
        </FormControl>
      </Box>
 
      {fileType === "policy" && (
        <Box>
          <FormControl variant="outlined" sx={{ mb: 2, width: 300 , ml:"-10px"}}>
             <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "19.6px",
                    textAlign: "left",
                    marginBottom: "8px",
                  }}
                >
                  Select Policy
                </Typography>
            <Select
                value={selectedPolicyId}
                onChange={handlePolicyChange}
              >
              {safeCustomizedForm.map((policy) => (
                <MenuItem key={policy.id} value={policy.id}>
                  {policy.policyNumber} {/* show meaningful label */}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
 
      <Box sx={{ display: 'flex', height: '100vh',mr:2,ml:-1 }}>
        <Box sx={{
          width: 300,
          borderRight: '1px solid #e0e0e0',
          height: '100%',
          p: 0,
          overflowY: 'auto'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,mr:1 }}>
            <Typography variant="h6">File Explorer</Typography>
            <Button
                variant="contained"
                startIcon={<CreateNewFolderIcon />}   
                size="small"
                onClick={() => {
                  setAddFolderModalOpen(true);
                  setNewFolderName("");
                }}
                disabled={
                  (fileType === "policy" && !selectedPolicyId) ||
                  (fileType === "customer" && !clientCustomerId)
                }
              >
                New Folder
              </Button>
          </Box>
 
          {folders && folders.length > 0 ? (
            <FolderTree
              ref={folderTreeRef}
              folders={folders}
              setFolders={setFolders}
              onFolderSelect={handleFolderSelect}
              selectedPath={selectedPath}
              openAddSubfolderModal={openAddSubfolderModal}
              handleDeleteFolder={openDeleteFolderModal}
              currentFolderId={currentFolderId}
              expandedFolders={expandedFolders}
              setExpandedFolders={setExpandedFolders}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {selectedPolicy || clientCustomerId ? "No folders available for this policy" : "Select a policy or customer to view folders"}
            </Typography>
          )}
        </Box>

        <Box sx={{ flexGrow: 1, height: '100%', overflowY: 'auto' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <FolderBreadcrumbs
              path={breadcrumbPath}
              onNavigate={handleBreadcrumbNavigation}
            />
          </Box>

          <FolderContents
          currentFolderId={currentFolderId}
            currentFolder={currentFolder}
            onOpenUploadPopup={() => setFileUploadPopupOpen(true)}
            handleDeleteFile={handleDeleteFile}
            onFolderSelect={handleFolderSelect}
            selectedPath={selectedPath}
          />
        </Box>

        <Modal open={addFolderModalOpen} onClose={() => setAddFolderModalOpen(false)} >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1
          }}>
            <Box  sx={{
              mb: 0,
              color: 'black',
              width: '432px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              px: 2, 
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }}>
            <Typography variant="h6" >Add New Folder</Typography>
                 </Box>
                 <Typography
                  variant="body1"
                >
                  Folder Name
                </Typography>
                <TextField
                  fullWidth
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  sx={{ mb: 3 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    onClick={() => setAddFolderModalOpen(false)}
                    variant="outlined"
                  >
                    Cancel ssss
                  </Button>
                  <Button
                    onClick={addNewFolder}
                    variant="contained"
                  >
                    Create
                  </Button>
                </Box>
              </Box>
            </Modal>

        {/* Add Subfolder Modal */}
          <Modal open={addSubfolderModalOpen} onClose={() => setAddSubfolderModalOpen(false)}>
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 1
            }}>
            <Box sx={{
                mb: 2,
                mt:-4,
                ml:-4,
                color: 'black',
                width: '432px', 
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                px: 2, 
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              }}>
                <Typography variant="h6" sx={{ mb: 0 }}>
                  Add Subfolder to {targetPath.length > 0 ? (targetPath[targetPath.length - 1].name || targetPath[targetPath.length - 1].folderName) : ""}
                </Typography>
              </Box>
              <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "19.6px",
                    textAlign: "left",
                    marginBottom: "8px",
                  }}
                >
                 Subfolder Name
              </Typography>
              <TextField
                fullWidth
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  onClick={() => setAddSubfolderModalOpen(false)}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  onClick={addNewSubfolder}
                  variant="contained"
                >
                  Create
                </Button>
              </Box>
            </Box>
          </Modal>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Modal open={deleteFolderModalOpen} onClose={() => setDeleteFolderModalOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 510,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1
          }}>
            <DialogTitle>Delete Folder</DialogTitle>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Are you sure you want to delete "{folderToDelete?.name || folderToDelete?.folderName}"?
              {folderToDelete?.subfolders?.length > 0 && (
                <Typography variant="subtitle2" color="error.main" sx={{ mt: 1 }}>
                  This folder contains {folderToDelete.subfolders.length} subfolder(s).
                </Typography>
              )}
              {folderToDelete?.files?.length > 0 && (
                <Typography variant="subtitle2" color="error.main" sx={{ mt: 1 }}>
                  This folder contains {folderToDelete.files.length} file(s).
                </Typography>
              )}
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                This action cannot be undone.
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={() => setDeleteFolderModalOpen(false)}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteFolder}
                variant="contained"
                color="error"
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>

        <Modal open={deleteFileModalOpen} onClose={() => setDeleteFileModalOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 510,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1
          }}>
            <DialogTitle>Delete File</DialogTitle>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Are you sure you want to delete "{fileToDelete?.filename || fileToDelete?.name}"?
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                This action cannot be undone.
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={() => {setDeleteFileModalOpen(false);
                setFileToDelete(null);
              }}
              variant="outlined">Cancel</Button>
              <Button  onClick={() => {
                confirmDeleteFile();
                setFileToDelete(null);
              }} variant="contained" color="error">
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>

        <FileUploadPopup
          open={fileUploadPopupOpen}
          onClose={() => setFileUploadPopupOpen(false)}
          onUpload={handleFileUpload}
          currentFolderId={currentFolderId}
          policyId={selectedPolicyId}
          customerId={clientCustomerId}
          fileType={fileType}
        />
      </Box>
    </Box>
  );
};

export default CustomerFileExplorer;

